import HTMLReactParser from "html-react-parser/lib/index";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { IoIosAddCircle } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import RenderToast from "../../Components/atoms/RenderToast/RenderToast";
import SideBarSkeleton from "../../Components/atoms/SideBarSkeleton";
import AreSureModal from "../../Components/molecules/AreSureModal";
import { BlogDetailModal } from "../../Components/molecules/BlogDetailModal";
import { BlogsModal } from "../../Components/molecules/BlogsModal";
import PopOver from "../../Components/molecules/PopOver/PopOver";
import SectionHeader from "../../Components/molecules/SectionHeader";
import Table from "../../Components/organisms/Table";
import { RECORDS_LIMIT } from "../../developmentContent/constants";
import { Delete, Get, Patch, Post } from "../../interceptor/axiosInterceptor";
import useDebounce from "../../resources/customHooks/useDebounce";
import {
  capitalizeFirstLetter,
  CreateFormData,
  htmlToText,
} from "../../resources/utils/helper";
import classes from "./Blogs.module.css";

export default function Blogs() {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState(""); // addModal, editModal, viewModal
  const [currentBlog, setCurrentBlog] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBlogToDelete, setSelectedBlogToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const debounceSearch = useDebounce(search);

  const onClickPopover = (label = "", dataItem = null) => {
    if (label === "Delete") {
      setSelectedBlogToDelete(dataItem);
      setShowDeleteModal(true);
    } else {
      setCurrentBlog(dataItem);
      setShowModal(label === "View" ? "viewModal" : "editModal");
    }
  };

  const BLOGS_POPOVER = [
    { label: "Edit" },
    { label: "View" },
    { label: "Delete", textClass: "text-danger" },
  ];

  const tableHeader = [
    { id: "title", label: "Title", style: { width: "25%" } },
    { id: "description", label: "Description", style: { width: "30%" } },
    { id: "createdAt", label: "Date", style: { width: "18%" } },
    {
      id: "status",
      label: "Status",
      style: { width: "17%" },
    },
    {
      id: "action",
      label: "Action",
      style: { width: "10%", textAlign: "center" },
      renderValue: (item, rowIndex) => {
        const newData = data?.find((e) => e?.slug === item?.id);
        return (
          <PopOver
            popover={BLOGS_POPOVER}
            onClick={(label) => onClickPopover(label, newData)}
          />
        );
      },
    },
  ];

  const tableBody = data?.map((item) => ({
    title: capitalizeFirstLetter(item?.title),
    description: htmlToText(item?.description),
    createdAt: moment(item?.createdAt || item?.date).format("DD-MM-YYYY"),
    status: item?.isActive ? "Active" : "Inactive",
    id: item?.slug,
  }));

  const getBlogsData = async ({ pg = page, _search = debounceSearch }) => {
    setLoading("loadings");
    const params = {
      page: pg,
      search: _search,
      limit: RECORDS_LIMIT,
    };

    const queryParams = new URLSearchParams(params)?.toString();
    const url = "admin/blog/all";

    const { response } = await Get({
      route: `${url}?${queryParams}`,
    });

    if (response) {
      setData(response?.data?.data);
      setPage(pg);
      setTotalRecords(response?.data?.totalRecords);
    }
    setLoading("");
  };

  const handleSubmit = async (values) => {
    const payload = {
      title: values?.title,
      description: values?.description,
      ...(values?.createdAt && {
        createdAt: values?.createdAt,
        date: values?.createdAt,
      }),
      ...(values?.thumbnailImage && { thumbnailImage: values?.thumbnailImage }),
      ...(values?.bannerImage && { bannerImage: values?.bannerImage }),
      ...(values?.thumbnailImage && { image: values?.thumbnailImage }),
      ...(values?.status?.label && {
        isActive: values?.status?.label === "Activate" ? true : false,
      }),
    };

    setLoading("postingBlog");

    const route = currentBlog?.slug
      ? `admin/blog/${currentBlog?.slug}`
      : "admin/blog";
    const apiMethod = currentBlog?.slug ? Patch : Post;
    const formData = CreateFormData(payload);

    const { response } = await apiMethod({
      route,
      data: formData,
      isFormData: true,
    });

    if (response) {
      RenderToast({
        type: "success",
        message: `Blog ${
          currentBlog?.slug ? "Updated" : "Added"
        } Successfully!`,
      });
      // Fetch updated data after submission
      getBlogsData({ pg: page, _search: debounceSearch });
      setCurrentBlog(null);
      setShowModal("");
    }

    setLoading("");
  };

  const handleDeleteBlog = async () => {
    if (!selectedBlogToDelete?.slug) return;
    setLoading("deletingBlog");

    const { response } = await Delete({
      route: `admin/blog/${selectedBlogToDelete?.slug}`,
      showAlert: true,
    });

    if (response) {
      RenderToast({
        type: "success",
        message: "Blog Deleted Successfully!",
      });
      setShowDeleteModal(false);
      setSelectedBlogToDelete(null);
      getBlogsData({ pg: page, _search: debounceSearch });
    }
    setLoading("");
  };

  useEffect(() => {
    setPage(1);
    getBlogsData({ pg: page, _search: debounceSearch });
  }, [debounceSearch]);

  return (
    <SideBarSkeleton>
      <Container fluid>
        <Row>
          <Col md={12}>
            <SectionHeader
              title="Blogs"
              showButton
              showSearch
              search={search}
              setSearch={setSearch}
              label="Add Blog"
              btnClass={classes.btnClass}
              leftIcon={<IoIosAddCircle size={20} />}
              variant="primary"
              onClick={() => {
                setCurrentBlog(null);
                setShowModal("addModal");
              }}
            />
          </Col>
          <Col md={12}>
            <div className={classes.tableDiv}>
              <Table
                currentPage={page}
                totalRecords={totalRecords}
                hasPagination
                headings={tableHeader}
                tableData={tableBody}
                {...{ totalRecords, currentPage: page }}
                loading={loading === "loadings"}
                setCurrentPage={(p) => {
                  setPage(p);
                  getBlogsData({ pg: p, _search: debounceSearch });
                }}
              />
            </div>
          </Col>
        </Row>
      </Container>

      {["addModal", "editModal"].includes(showModal) && (
        <BlogsModal
          showModal={["addModal", "editModal"].includes(showModal)}
          setShowModal={() => {
            setCurrentBlog(null);
            setShowModal("");
          }}
          loading={loading === "postingBlog"}
          data={currentBlog}
          handleSubmit={handleSubmit}
        />
      )}
      {showModal == "viewModal" && (
        <BlogDetailModal
          showModal={showModal == "viewModal"}
          setShowModal={() => {
            setCurrentBlog(null);
            setShowModal("");
          }}
          data={currentBlog}
        />
      )}
      {showDeleteModal && (
        <AreSureModal
          show={showDeleteModal}
          setShow={setShowDeleteModal}
          message="Are you sure you want to delete this blog?"
          onYes={handleDeleteBlog}
          isLoading={loading === "deletingBlog"}
        />
      )}
    </SideBarSkeleton>
  );
}
